// React and related imports
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Flowbite components
import { Table } from 'flowbite-react';

// Custom components
import { TableCell, TableCommon } from '../components/Table/Table';
import { Loading } from '../components/Shared/Loading/Loading';
import { PageHeader } from '../components/Shared/PageHeader';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { StatusBadge } from '../components/Shared/StatusBadge/StatusBadge';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';

// i18next for translations
import { useTranslation } from 'react-i18next';

// Redux API hooks
import { useGetBroadcastsQuery } from '../redux/api/broadcastApi';

// Interfaces and enums
import { IBroadcast } from '../common/interfaces/Broadcast.interface';
import { BroadcastStatus } from '../common/enums/BroadcastStatus';

// Custom hooks
import { usePageTitle } from '../hooks/usePageTitle';

// Utils
import { getLocalizedValueFromMultiLangValues } from '../utils/getLocalizedValueFromMultiLangValues';
import { formatDate } from '../utils/dateUtils';
import { getBroadcastStatusVariant } from '../components/Broadcast/utils';

export const BroadcastListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  usePageTitle(t('pageTitles.broadcast'));

  const [shouldPoll, setShouldPoll] = useState(true);

  const {
    data: broadcastsResponse,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useGetBroadcastsQuery(
    {
      page: 1,
      limit: 100,
    },
    {
      refetchOnMountOrArgChange: true, // Fetch fresh data on mount and refresh
      pollingInterval: shouldPoll ? 2000 : 0, // Poll every 2 seconds only if shouldPoll is true
    }
  );

  const broadcasts = broadcastsResponse?.data;

  // Check if there are any active broadcasts and update polling state
  useEffect(() => {
    if (broadcasts) {
      const hasActiveBroadcasts = broadcasts.some(
        (broadcast: IBroadcast) =>
          broadcast.status === BroadcastStatus.NEW ||
          broadcast.status === BroadcastStatus.SCHEDULING ||
          broadcast.status === BroadcastStatus.IN_PROGRESS
      );
      setShouldPoll(hasActiveBroadcasts);
    }
  }, [broadcasts]);

  const columnHeadLabels = [
    { id: 'title', label: t('broadcast.title') },
    { id: 'targetUsers', label: t('broadcast.targetUsers') },
    { id: 'status', label: t('broadcast.statusColumn') },
    { id: 'createdBy', label: t('broadcast.createdBy') },
    { id: 'createdAt', label: t('broadcast.createdAt') },
    { id: 'actions', label: t('broadcast.actions') },
  ];

  const handleCreateClick = () => {
    navigate('/broadcast/create');
  };

  const handleViewStats = (broadcastId: string) => {
    navigate(`/broadcast/${broadcastId}/stats`);
  };

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title={t('broadcast.broadcastManagement')}
        buttonText={t('broadcast.createBroadcast')}
        buttonOnClick={handleCreateClick}
      />

      {broadcasts && broadcasts.length > 0 ? (
        <TableCommon columns={columnHeadLabels}>
          {broadcasts.map((broadcast: IBroadcast) => (
            <Table.Row key={broadcast.id}>
              <TableCell>
                {getLocalizedValueFromMultiLangValues(broadcast.title)}
              </TableCell>
              <TableCell>{broadcast.noOfTargetUsers}</TableCell>
              <TableCell>
                <StatusBadge
                  variant={getBroadcastStatusVariant(broadcast.status)}
                >
                  {t(`broadcast.status.${broadcast.status}`)}
                </StatusBadge>
              </TableCell>
              <TableCell>
                {broadcast.createdBy?.name || t('broadcast.unknown')}
              </TableCell>
              <TableCell>{formatDate(broadcast.createdAt)}</TableCell>
              <TableCell>
                <button
                  onClick={() => handleViewStats(broadcast.id)}
                  className="text-primary-600 hover:underline dark:text-primary-300"
                >
                  {t('broadcast.viewStats')}
                </button>
              </TableCell>
            </Table.Row>
          ))}
        </TableCommon>
      ) : (
        <EmptyState title={t('broadcast.noBroadcasts')} />
      )}
    </>
  );
};
